import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, User, BookOpen } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Button from '../components/ui/Button';
// isomorphic : DOMPurify navigateur côté client, jsdom au build (SSR) — la
// même sanitisation des deux côtés évite tout désaccord d'hydratation.
import DOMPurify from 'isomorphic-dompurify';
import { blogService, resolveImageUrl } from '../services/blogService';
import type { BlogArticle as BlogArticleType, BlogArticleListItem } from '../types/blog';

const SITE_URL = 'https://douxcemoman.fr';

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

/**
 * Assainit le HTML de l'article et résout les images insérées dans le corps.
 *
 * Les images uploadées depuis l'éditeur sont stockées avec une URL relative
 * (`/api/uploads/...`). Sans résolution, le navigateur les requête sur l'origine
 * du front (≠ backend en dev / hôte SSG) → 404. On les fait passer par
 * resolveImageUrl, exactement comme l'image de couverture.
 */
const sanitizeArticleHtml = (html: string): string => {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'IMG') {
      const src = node.getAttribute('src');
      if (src && src.startsWith('/')) {
        node.setAttribute('src', resolveImageUrl(src) as string);
      }
    }
  });
  const clean = DOMPurify.sanitize(html);
  DOMPurify.removeHook('afterSanitizeAttributes');
  return clean;
};

/**
 * Page article du Mag'Moman — route DYNAMIQUE, volontairement hors du SSG figé.
 *
 * Le contenu éditorial change entre deux builds (créé/édité au back office). On
 * NE l'attache donc PAS à un `loader` React Router : sinon vite-react-ssg
 * servirait, en navigation interne depuis une page pré-rendue, des données
 * figées dans le manifeste du build (un article créé après le build serait
 * « introuvable » jusqu'au prochain déploiement). Ici on récupère toujours
 * l'article en live, côté client. Le SEO est couvert séparément (pré-rendu à la
 * demande / petit SSR du <head>), pas par un snapshot de build.
 */
const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [article, setArticle] = useState<BlogArticleType | null>(null);
  const [related, setRelated] = useState<BlogArticleListItem[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound'>('loading');

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setStatus('loading');
    setArticle(null);

    // getBySlug retente déjà en interne (backoff + cache-bust pour contourner un
    // cold start backend ou une réponse mise en cache trop tôt). On ne conclut
    // « introuvable » qu'après son échec définitif.
    (async () => {
      try {
        const a = await blogService.getBySlug(slug);
        if (cancelled) return;
        setArticle(a);
        setStatus('ready');
        try {
          const list = await blogService.listPublished();
          if (!cancelled) setRelated(list.filter((x) => x.slug !== slug).slice(0, 3));
        } catch {
          /* suggestions optionnelles */
        }
      } catch {
        if (!cancelled) setStatus('notfound');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!article) {
    // Tant que le chargement est en cours, on affiche un état d'attente
    // (et surtout pas « introuvable », qui serait un faux négatif).
    if (status === 'loading') {
      return (
        <div className="pt-24 pb-16 min-h-screen bg-neutral-offwhite">
          <SEOHead title="Chargement…" path={`/blog/${slug}`} noindex />
          <p className="text-center text-neutral-body font-nunito py-16">Chargement de l'article…</p>
        </div>
      );
    }
    return (
      <div className="pt-24 pb-16 min-h-screen bg-neutral-offwhite">
        <SEOHead title="Article introuvable" path={`/blog/${slug}`} noindex />
        <div className="container mx-auto px-4 text-center py-16">
          <h1 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">Article introuvable</h1>
          <p className="font-nunito text-neutral-body mb-8">
            Cet article n'existe pas ou n'est plus disponible.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-brand font-nunito font-semibold hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au Mag'Moman
          </Link>
        </div>
      </div>
    );
  }

  const coverImage = resolveImageUrl(article.cover_image_url);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt || undefined,
    image: coverImage || undefined,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at,
    author: { '@type': 'Organization', name: article.author || 'Doux ce Moman' },
    publisher: {
      '@type': 'Organization',
      name: 'Doux ce Moman',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${article.slug}` },
  };

  return (
    <article className="pt-24 pb-16 bg-neutral-offwhite min-h-screen">
      <SEOHead
        title={article.title}
        description={article.excerpt || `${article.title} — Mag'Moman, le blog de Doux ce Moman.`}
        path={`/blog/${article.slug}`}
        image={coverImage}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="container mx-auto px-4 max-w-3xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-brand font-nunito font-semibold text-sm hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au Mag'Moman
        </Link>

        <header className="mb-8">
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-neutral-ink mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-body/80 font-nunito">
            {article.published_at && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> {formatDate(article.published_at)}
              </span>
            )}
            {article.author && (
              <span className="inline-flex items-center gap-1.5">
                <User className="w-4 h-4" /> {article.author}
              </span>
            )}
          </div>
        </header>

        {coverImage && (
          <img
            src={coverImage}
            alt={article.title}
            className="w-full rounded-2xl mb-10 object-cover"
          />
        )}

        {/* Contenu HTML rédigé depuis le back office (sanitisé) */}
        <div
          className="blog-content font-nunito text-neutral-body leading-relaxed space-y-4"
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(article.content) }}
        />

        {/* Appel à l'action : réserver une séance */}
        <div className="mt-14 rounded-3xl bg-gradient-to-br from-accent-rose/15 via-accent-sand/15 to-brand/10 border border-brand/10 p-8 sm:p-10 text-center">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-neutral-ink mb-3">
            Envie de vivre ce moment avec votre bébé&nbsp;?
          </h2>
          <p className="font-nunito text-neutral-body max-w-xl mx-auto mb-6">
            Offrez-vous une parenthèse de douceur dans notre cocon de Geneuille, près de Besançon.
          </p>
          <Button variant="primary" size="lg" to="/reservation">
            Réserver une séance
          </Button>
        </div>

        {/* Retour en bas de page */}
        <div className="mt-12 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-brand font-nunito font-semibold text-sm hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au Mag'Moman
          </Link>
        </div>
      </div>

      {/* À lire aussi — masqué s'il n'y a pas d'autre article */}
      {related.length > 0 && (
        <section className="container mx-auto px-4 max-w-5xl mt-16 pt-12 border-t border-neutral-line/60">
          <h2 className="font-playfair text-2xl font-bold text-neutral-ink mb-8 text-center">
            À lire aussi
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((a) => (
              <Link
                key={a.id}
                to={`/blog/${a.slug}`}
                className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden bg-accent-sand/30">
                  {a.cover_image_url ? (
                    <img
                      src={resolveImageUrl(a.cover_image_url)}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-accent-rose/40" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col flex-grow p-6">
                  {a.published_at && (
                    <div className="flex items-center gap-2 text-xs text-neutral-body/70 font-nunito mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(a.published_at)}
                    </div>
                  )}
                  <h3 className="font-playfair text-lg font-bold text-neutral-ink mb-2 group-hover:text-brand transition-colors">
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p className="font-nunito text-neutral-body text-sm leading-relaxed line-clamp-3 mb-4">
                      {a.excerpt}
                    </p>
                  )}
                  <span className="mt-auto inline-flex items-center gap-1.5 text-brand font-nunito font-semibold text-sm">
                    Lire l'article
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogArticlePage;
